import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import logger, { getCorrelationId } from '@/lib/logging';
import { apiFetch } from '@/lib/api-client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { HeartIcon } from "../icons";
import { Checkbox } from "../ui/checkbox";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { auth } from "@/lib/firebase-config";
import { useSettings } from "@/components/settings/lib/settings-context";
import { Copy, Mic, Square, Loader2, Circle, Video, CheckCircle2, XCircle } from "lucide-react";
import * as ActivityLogger from "@/lib/activity-logger";
import { cn } from "@/lib/utils";
import { gatherSystemInfo, formatSystemInfoAsMarkdown } from "./lib/system-info";
import type { LogEntry } from "@/lib/activity-logger";
// Firebase storage imports removed as we are switching to Slack via API
// import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
// import { storage } from "@/lib/firebase-config";

// Removed direct flow imports to avoid client-side bundling issues
// import { summarizeActivityFlow } from "@/ai/flows/summary-flow";
// import { fillFormFromVoiceFlow } from "@/ai/flows/voice-fill-flow";
// Define types locally or import from shared types if available.
// For now duplicating minimal types to avoid importing server files.
type SummaryFlowOutput = {
  type: 'bug' | 'feature';
  title: string;
  bugFields?: { steps: string; expected: string; actual: string };
  featureFields?: { description: string; howItHelps: string };
};
type VoiceFillFormOutput = {
  title?: string;
  steps?: string;
  expected?: string;
  actual?: string;
  description?: string;
  howItHelps?: string;
};

type FeedbackType = "bug" | "feature";

interface GitHubIssueInput {
  title: string;
  type: 'bug' | 'feature';
  section: string;
  description?: string;
  steps?: string;
  expected?: string;
  actual?: string;
  howItHelps?: string;
  contactEmail?: string;
}

const getCookie = (name: string): string => {
  if (typeof document === 'undefined') return '';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
  return '';
};

const setCookie = (name: string, value: string, days: number) => {
  if (typeof document === 'undefined') return;
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

export function FeedbackButton() {
  const location = useLocation();
  const pathname = location.pathname;

  // UI States
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Initialize from singleton state to persist across navigation
  const [isRecording, setIsRecording] = useState(() => ActivityLogger.isRecording());
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [includeVideo, setIncludeVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoUploadStatus, setVideoUploadStatus] = useState<'idle' | 'uploading' | 'done' | 'failed' | 'skipped'>('idle');
  const [submitWithoutWaitingForVideo, setSubmitWithoutWaitingForVideo] = useState(false);

  // Form States
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [howItHelps, setHowItHelps] = useState("");
  const [allowContact, setAllowContact] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [sourcePath, setSourcePath] = useState<string>(pathname);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const { settings, setSettings } = useSettings();

  // Audio Recording Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Video Recording Refs
  const screenRecorderRef = useRef<MediaRecorder | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const screenChunksRef = useRef<Blob[]>([]);
  const screenRecordingStartTimeRef = useRef<number | null>(null);

  // Minimum recording duration (ms) and minimum blob size (bytes) to accept a video
  const MIN_RECORDING_DURATION_MS = 2000;
  const MIN_BLOB_SIZE_BYTES = 1024;

  // Logs captured during recording, persisted for create-issue submission
  const logsForSubmissionRef = useRef<LogEntry[]>([]);
  // Slack video permalink; ref ensures it's available at submit time even if state is stale
  const videoUrlForSubmissionRef = useRef<string | null>(null);

  useEffect(() => {
    // Check if undefined to fix lint error
    if (typeof window !== 'undefined' && auth) {
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        if (currentUser) {
          setContactEmail(currentUser.email || "");
          setAllowContact(settings.allowContact ?? false);
        } else {
          setAllowContact(getCookie("allowContact") === "true");
          setContactEmail("");
        }
      });
      return () => unsubscribe();
    }
  }, [auth, settings.allowContact]);

  const handleAllowContactChange = (checked: boolean) => {
    setAllowContact(checked);
    if (user) {
      setSettings(s => ({ ...s, allowContact: checked }));
    } else {
      setCookie("allowContact", String(checked), 365);
    }
  };

  const startRecordingFlow = async () => {
    setIsPopoverOpen(false);
    setVideoUploadStatus('idle');
    setSubmitWithoutWaitingForVideo(false);

    // Start ActivityLogger and optional screen recording
    if (includeVideo && settings.experimentalFeaturesEnabled) {
      try {
        // Prefer tab-only capture (displaySurface: 'browser') for reliability
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' } as any,
          audio: false,
        });
        screenStreamRef.current = stream;
        const mediaRecorder = new MediaRecorder(stream);
        screenRecorderRef.current = mediaRecorder;
        screenChunksRef.current = [];
        screenRecordingStartTimeRef.current = Date.now();

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            screenChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.start();

        stream.getVideoTracks()[0].onended = () => {
          if (isRecording) stopRecordingFlow();
        };
      } catch (err) {
        logger.error("Error starting screen recording:", err);
        toast({ title: "User Actions Recording Failed", description: "Could not share screen. Recording logs only.", variant: "destructive" });
      }
    }

    ActivityLogger.startRecording(user?.uid, user?.email || undefined, 'user');
    setIsRecording(true);
    toast({
      title: "Recording Started",
      description: includeVideo && settings.experimentalFeaturesEnabled
        ? "Video uploads to Slack. AI analyzes logs only (not video). Submit when ready."
        : "Interact with the app to capture your issue. Click the recording button again to stop.",
    });
  };

  const stopRecordingFlow = async () => {
    setIsRecording(false);
    setIsAnalyzing(true);

    const logs = ActivityLogger.stopRecording();
    logsForSubmissionRef.current = logs;

    logger.info(`[FeedbackButton] Stop recording: ${logs.length} logs, path=${pathname}`);
    if (logs.length === 0) {
      logger.warn(`[FeedbackButton] ⚠️ No logs captured`);
    }

    // Flush and stop MediaRecorder so we capture final chunks; stop display stream only after we have the blob
    const stream = screenStreamRef.current;
    const recorder = screenRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      try {
        recorder.requestData();
      } catch {
        // requestData() can throw in some environments; continue with stop()
      }
      recorder.stop();
    }

    // Prepare video blob (wait for recorder to finish so chunks are captured) then stop browser screen share
    let videoBlob: Blob | null = null;
    if (includeVideo && screenRecorderRef.current) {
      const rec = screenRecorderRef.current;
      await new Promise<void>((resolve) => {
        if (rec.state === 'inactive') {
          resolve();
          return;
        }
        rec.onstop = () => resolve();
        setTimeout(resolve, 1500);
      });
      // Allow time for final dataavailable to fire and push to screenChunksRef
      await new Promise((r) => setTimeout(r, 600));
      videoBlob = new Blob(screenChunksRef.current, { type: 'video/webm' });
    }

    // Now stop display stream so browser exits "You are sharing your screen"
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    if (includeVideo && videoBlob !== null) {
      // Check minimum recording duration
      const recordingDurationMs = screenRecordingStartTimeRef.current
        ? Date.now() - screenRecordingStartTimeRef.current
        : 0;
      screenRecordingStartTimeRef.current = null; // reset

      if (recordingDurationMs > 0 && recordingDurationMs < MIN_RECORDING_DURATION_MS) {
        setVideoUploadStatus('skipped');
        logger.warn(`[FeedbackButton] [VideoUpload] ⚠️ Recording too short (${recordingDurationMs}ms < ${MIN_RECORDING_DURATION_MS}ms minimum). Skipping upload.`);
        toast({ title: "Recording Too Short", description: "User actions recording must be at least 2 seconds. Video was discarded.", variant: "destructive" });
      } else if (videoBlob.size < MIN_BLOB_SIZE_BYTES) {
        setVideoUploadStatus('skipped');
        logger.warn(`[FeedbackButton] [VideoUpload] ⚠️ Video blob too small (${videoBlob.size} bytes < ${MIN_BLOB_SIZE_BYTES} minimum); screen chunks count=${screenChunksRef.current.length}`);
        toast({ title: "Recording Empty", description: "User actions recording was empty or too small. Try sharing a browser tab instead of the entire screen.", variant: "destructive" });
      } else {
        setVideoUploadStatus('uploading');
        toast({ title: "Uploading Video...", description: "Video uploads to Slack. AI analyzes logs only (not video). Submit when ready." });
        // Fire upload in parallel; don't await
        (async () => {
          try {
            const formData = new FormData();
            const timestamp = new Date().getTime();
            const videoFileName = `screen-rec-${user?.uid || 'anon'}-${timestamp}.webm`;
            formData.append('video', videoBlob!, videoFileName);
            logger.info(`[FeedbackButton] [VideoUpload] Starting upload: blob size=${videoBlob!.size} bytes (${Math.round(videoBlob!.size / 1024)}KB), duration=${recordingDurationMs}ms, filename=${videoFileName}`);

            const uploadResult = await apiFetch<{ permalink?: string }>(
              '/feedback/upload-slack-video',
              { method: 'POST', body: formData }
            );

            if (uploadResult.permalink) {
              setVideoUrl(uploadResult.permalink);
              videoUrlForSubmissionRef.current = uploadResult.permalink;
              setVideoUploadStatus('done');
              logger.info(`[FeedbackButton] [VideoUpload] ✅ Video uploaded to Slack: ${uploadResult.permalink}`);
            } else {
              logger.warn('[FeedbackButton] [VideoUpload] ⚠️ No permalink in upload response:', JSON.stringify(uploadResult));
              setVideoUploadStatus('failed');
            }
          } catch (err) {
            logger.error("[FeedbackButton] [VideoUpload] ❌ Exception during upload:", err instanceof Error ? err.message : String(err));
            setVideoUploadStatus('failed');
            toast({ title: "Video Upload Failed", description: "Could not upload user actions recording to Slack.", variant: "destructive" });
          }
        })();
      }
    } else {
      setVideoUploadStatus('skipped');
    }

    try {
      // AI Summary via API
      const payloadSize = JSON.stringify(logs).length;
      const payloadString = JSON.stringify(logs);
      logger.info(`[FeedbackButton] Sending summarize request: ${logs.length} logs, ~${Math.round(payloadSize / 1024)}KB`);
      
      if (!navigator.onLine) {
        logger.error(`[FeedbackButton] ❌ Network is offline!`);
        throw new Error('Network is offline. Please check your internet connection.');
      }
      
      const requestStartTime = Date.now();

      let summary: SummaryFlowOutput;
      try {
        summary = await apiFetch<SummaryFlowOutput>('/feedback/summarize', {
          method: 'POST',
          body: payloadString,
        });
      } catch (fetchError: unknown) {
        const message = fetchError instanceof Error ? fetchError.message : String(fetchError);
        logger.error(`[FeedbackButton] ❌ Fetch failed: ${message}`);
        throw new Error(`Network request failed: ${message}`);
      }

      const requestDuration = Date.now() - requestStartTime;
      logger.info(`[FeedbackButton] Response received in ${requestDuration}ms`);

      const summaryPreview = JSON.stringify(summary);
      logger.debug(`[FeedbackButton] Summary: ${summaryPreview.substring(0, 150)}${summaryPreview.length > 150 ? '...' : ''}`);
      
      // Handle case where response might be nested (AI sometimes wraps in 'analysis' key)
      if (summary && typeof summary === 'object' && 'analysis' in summary && !('type' in summary)) {
        logger.warn(`[FeedbackButton] ⚠️ Summary is nested under 'analysis' key, transforming...`);
        const analysis = (summary as any).analysis;
        
        // Transform nested structure to expected format
        const classification = analysis.classification || analysis.type || 'feature';
        const isBug = classification === 'bug' || (analysis.reasoning && analysis.reasoning.toLowerCase().includes('error'));
        
        summary = {
          type: isBug ? 'bug' : 'feature',
          title: analysis.title || analysis.summary?.substring(0, 100) || 'User Activity Summary',
          ...(isBug ? {
            bugFields: {
              steps: analysis.steps || analysis.activity_summary || 'See activity logs',
              expected: analysis.expected || 'Expected behavior not specified',
              actual: analysis.actual || analysis.summary || 'See activity logs'
            }
          } : {
            featureFields: {
              description: analysis.description || analysis.activity_summary || analysis.summary || 'Feature request based on user activity',
              howItHelps: analysis.howItHelps || analysis.reasoning || 'Would improve user workflow'
            }
          })
        };
        logger.info(`[FeedbackButton] ✅ Transformed nested response to expected format`);
      }
      
      logger.info(`[FeedbackButton] Summary: type=${summary.type || 'MISSING'}, title=${summary.title?.length || 0} chars`);
      
      // Validate summary structure
      const validationErrors: string[] = [];
      
      if (!summary.type || (summary.type !== 'bug' && summary.type !== 'feature')) {
        validationErrors.push(`Invalid type: ${summary.type}`);
        logger.error(`[FeedbackButton] ❌ Invalid type: ${summary.type}`);
      }
      
      if (!summary.title || summary.title.trim().length === 0) {
        validationErrors.push(`Empty title`);
        logger.warn(`[FeedbackButton] ⚠️ Empty title, using default`);
        summary.title = summary.type === 'bug' ? 'Bug Report' : 'Feature Request';
      }
      
      if (validationErrors.length > 0) {
        logger.warn(`[FeedbackButton] ⚠️ Validation: ${validationErrors.join(', ')}`);
      }
      
      // Detailed logging of summary content
      if (summary.type === 'bug' && summary.bugFields) {
        logger.info(`[FeedbackButton] Bug fields: steps=${summary.bugFields.steps?.length || 0} expected=${summary.bugFields.expected?.length || 0} actual=${summary.bugFields.actual?.length || 0} chars`);
        
        // Ensure all bug fields are non-empty
        if (!summary.bugFields.steps || summary.bugFields.steps.trim().length === 0) {
          logger.warn(`[FeedbackButton] ⚠️ Bug summary has empty steps field!`);
          summary.bugFields.steps = 'See activity logs for details';
        }
        if (!summary.bugFields.expected || summary.bugFields.expected.trim().length === 0) {
          logger.warn(`[FeedbackButton] ⚠️ Bug summary has empty expected field!`);
          summary.bugFields.expected = 'Expected behavior not specified';
        }
        if (!summary.bugFields.actual || summary.bugFields.actual.trim().length === 0) {
          logger.warn(`[FeedbackButton] ⚠️ Bug summary has empty actual field!`);
          summary.bugFields.actual = 'See activity logs for details';
        }
      } else if (summary.type === 'feature' && summary.featureFields) {
        logger.info(`[FeedbackButton] Feature fields: description=${summary.featureFields.description?.length || 0} howItHelps=${summary.featureFields.howItHelps?.length || 0} chars`);
        
        // Ensure all feature fields are non-empty
        if (!summary.featureFields.description || summary.featureFields.description.trim().length === 0) {
          logger.warn(`[FeedbackButton] ⚠️ Feature summary has empty description field!`);
          summary.featureFields.description = 'See activity logs for details';
        }
        if (!summary.featureFields.howItHelps || summary.featureFields.howItHelps.trim().length === 0) {
          logger.warn(`[FeedbackButton] ⚠️ Feature summary has empty howItHelps field!`);
          summary.featureFields.howItHelps = 'Would improve user workflow';
        }
      } else {
        logger.warn(`[FeedbackButton] ⚠️ Summary missing required fields`);
        
        // Provide fallback values
        if (summary.type === 'bug' && !summary.bugFields) {
          logger.warn(`[FeedbackButton] ⚠️ Adding fallback bugFields`);
          summary.bugFields = {
            steps: 'See activity logs for details',
            expected: 'Expected behavior not specified',
            actual: 'See activity logs for details'
          };
        } else if (summary.type === 'feature' && !summary.featureFields) {
          logger.warn(`[FeedbackButton] ⚠️ Adding fallback featureFields`);
          summary.featureFields = {
            description: 'See activity logs for details',
            howItHelps: 'Would improve user workflow'
          };
        }
      }

      try {
        setFeedbackType(summary.type);
        setTitle(summary.title || '');
        setSourcePath(pathname);
      } catch (err: any) {
        logger.error(`[FeedbackButton] Failed to set form: ${err.message}`);
      }

      if (summary.type === 'bug' && summary.bugFields) {
        try {
          setSteps(summary.bugFields.steps || '');
          setExpected(summary.bugFields.expected || '');
          setActual(summary.bugFields.actual || '');
        } catch (err: any) {
          logger.error(`[FeedbackButton] Failed to set bug fields: ${err.message}`);
        }
      } else if (summary.type === 'feature' && summary.featureFields) {
        try {
          setDescription(summary.featureFields.description || '');
          setHowItHelps(summary.featureFields.howItHelps || '');
        } catch (err: any) {
          logger.error(`[FeedbackButton] Failed to set feature fields: ${err.message}`);
        }
      } else {
        logger.warn(`[FeedbackButton] ⚠️ Could not populate form - missing fields (type=${summary.type})`);
      }

      setIsSheetOpen(true);
      toast({
        title: "Analysis Complete",
        description: "We've drafted a report based on your activity.",
      });

    } catch (error) {
      logger.error(`[FeedbackButton] ❌ ERROR in stopRecordingFlow`);
      logger.error(`[FeedbackButton] ════════════════════════════════════════`);
      logger.error(`[FeedbackButton] ERROR DETAILS:`);
      logger.error(`[FeedbackButton]   - Error type: ${error instanceof Error ? error.name : typeof error}`);
      logger.error(`[FeedbackButton]   - Error message: ${error instanceof Error ? error.message : String(error)}`);
      
      if (error instanceof Error) {
        logger.error(`[FeedbackButton]   - Error stack:`, error.stack?.substring(0, 1000));
        
        // Check for specific error types
        if (error.message.includes('Network') || error.message.includes('fetch')) {
          logger.error(`[FeedbackButton]   ⚠️ Network error detected`);
          logger.error(`[FeedbackButton]   - Check internet connection`);
          logger.error(`[FeedbackButton]   - Check if server is running`);
          logger.error(`[FeedbackButton]   - Check CORS settings`);
        }
        
        if (error.message.includes('JSON') || error.message.includes('parse')) {
          logger.error(`[FeedbackButton]   ⚠️ JSON parsing error detected`);
          logger.error(`[FeedbackButton]   - Server may have returned invalid JSON`);
          logger.error(`[FeedbackButton]   - Check server logs for response format`);
        }
        
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          logger.error(`[FeedbackButton]   ⚠️ Authentication error detected`);
          logger.error(`[FeedbackButton]   - User may need to re-login`);
          logger.error(`[FeedbackButton]   - Check session cookie`);
        }
        
        if (error.message.includes('500') || error.message.includes('Internal Server')) {
          logger.error(`[FeedbackButton]   ⚠️ Server error detected`);
          logger.error(`[FeedbackButton]   - Check server logs for details`);
          logger.error(`[FeedbackButton]   - This may be an AI generation failure`);
        }
      }
      
      logger.error(`[FeedbackButton] Full error object:`, {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
        cause: error instanceof Error && (error as any).cause ? (error as any).cause : undefined
      });
      logger.error(`[FeedbackButton] ════════════════════════════════════════`);
      
      const is503 = error instanceof Error && error.message.includes('503');
      toast({
        title: "Analysis Failed",
        description: is503
          ? "The dev server may be restarting (common when editing files). Wait a few seconds and try again, or fill the form manually."
          : "Could not summarize activity. Please fill the form manually.",
        variant: "destructive"
      });
      setIsSheetOpen(true); // Still open form
      logger.info(`[FeedbackButton] ════════════════════════════════════════`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleVoiceRecording = async () => {
    if (isVoiceRecording) {
      // Stop Voice Recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } else {
      // Start Voice Recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

          // Convert Blob to Base64 Data URI
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = async () => {
            const base64Audio = reader.result as string;

            setIsAnalyzing(true);
            try {
              const updatedFields = await apiFetch<VoiceFillFormOutput>('/feedback/voice-fill', {
                method: 'POST',
                body: JSON.stringify({
                  audioDataUri: base64Audio,
                  currentType: feedbackType,
                  currentFields: {
                    title,
                    steps,
                    expected,
                    actual,
                    description,
                    howItHelps
                  }
                }),
              });

              // Merge updates
              if (updatedFields.title) setTitle(updatedFields.title);
              if (feedbackType === 'bug') {
                if (updatedFields.steps) setSteps(updatedFields.steps);
                if (updatedFields.expected) setExpected(updatedFields.expected);
                if (updatedFields.actual) setActual(updatedFields.actual);
              } else {
                if (updatedFields.description) setDescription(updatedFields.description);
                if (updatedFields.howItHelps) setHowItHelps(updatedFields.howItHelps);
              }

              toast({
                title: "Voice Transcribed",
                description: "Form updated with your voice input.",
              });
            } catch (error) {
              logger.error("Voice processing failed:", error);
              toast({
                title: "Voice Processing Failed",
                description: "Could not process your voice input.",
                variant: "destructive"
              });
            } finally {
              setIsAnalyzing(false);
              // Stop all tracks to release mic
              stream.getTracks().forEach(track => track.stop());
            }
          };
        };

        mediaRecorder.start();
        setIsVoiceRecording(true);
        toast({
          title: "Recording Started",
          description: "Please click 'Stop sharing' in your browser/system UI when you are finished.",
        });
      } catch (err) {
        logger.error("Error accessing microphone:", err);
        toast({
          title: "Microphone Error",
          description: "Could not access microphone.",
          variant: "destructive"
        });
      }
    }
  };

  // Clean up voice recording state when stopping
  useEffect(() => {
    if (!isVoiceRecording && mediaRecorderRef.current) {
      // logic handled in onstop
    }
  }, [isVoiceRecording]);

  const handleVoiceStop = () => {
    setIsVoiceRecording(false);
  };


  const handleSubmit = async () => {
    // Validation logic
    if (feedbackType === 'bug') {
      if (!title || !steps || !expected || !actual) {
        toast({ title: "Missing Information", description: "Please fill out all bug report fields.", variant: "destructive" });
        return;
      }
    } else {
      if (!title || !description || !howItHelps) {
        toast({ title: "Missing Information", description: "Please fill out all feature request fields.", variant: "destructive" });
        return;
      }
    }

    if (allowContact && !contactEmail) {
      toast({ title: "Missing Email", description: "Please provide an email.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      // Build request body based on feedback type
      const requestBody: any = {
        title,
        type: feedbackType,
        section: sourcePath,
      };

      if (feedbackType === 'bug') {
        requestBody.steps = steps;
        requestBody.expected = expected;
        requestBody.actual = actual;
      } else {
        requestBody.description = description;
        requestBody.howItHelps = howItHelps;
      }

      if (allowContact && contactEmail) {
        requestBody.contactEmail = contactEmail;
      }

      // Attach screen recording (use ref as fallback—state can be stale across async flow)
      const urlToUse = videoUrl ?? videoUrlForSubmissionRef.current;
      if (urlToUse) {
        requestBody.videoUrl = urlToUse;
      }
      requestBody.correlationId = getCorrelationId();
      const storedLogs = logsForSubmissionRef.current;
      const systemInfo = gatherSystemInfo(user?.uid, user?.email ?? undefined, storedLogs.length > 0 ? storedLogs : undefined);
      // Limit logs to avoid exceeding GitHub issue body limit (65,536 chars); keep last 80 for debugging
      const systemInfoMarkdown = formatSystemInfoAsMarkdown(systemInfo, { maxLogs: 80 });
      if (systemInfoMarkdown.trim()) {
        requestBody.systemInfoMarkdown = systemInfoMarkdown;
      }

      // Call server API to create GitHub issue
      const result = await apiFetch<{ success: boolean; issueUrl?: string; error?: string }>(
        '/feedback/create-issue',
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
        }
      );

      if (result.success && result.issueUrl) {
        // Success Toast & Copy logic
        navigator.clipboard.writeText(result.issueUrl);
        toast({ title: "Feedback Submitted", description: "Issue created and URL copied." });
      } else {
        throw new Error(result.error || 'Failed to create issue');
      }

      setIsSheetOpen(false);
      // Reset form
      setTitle(""); setDescription(""); setSteps(""); setExpected(""); setActual(""); setHowItHelps("");
      setVideoUrl(null);
      setVideoUploadStatus('idle');
      setSubmitWithoutWaitingForVideo(false);
      videoUrlForSubmissionRef.current = null;
      setIncludeVideo(false);
      screenChunksRef.current = [];
      logsForSubmissionRef.current = [];

    } catch (error) {
      logger.error("Failed to submit feedback:", error);
      toast({ title: "Submission Failed", description: error instanceof Error ? error.message : "Error submitting feedback.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Recording Button if recording (show Stop for both user and diagnostic-managed sessions)
  if (isRecording) {
    return (
      <div className="flex flex-col items-center gap-1.5" data-testid="recording-active-container">
        <Button
          variant="destructive"
          size="sm"
          className="shadow-lg hover:shadow-xl transition-all"
          onClick={stopRecordingFlow}
        >
          <Square className="h-4 w-4 mr-2 fill-current" />
          Stop Recording
        </Button>
        {includeVideo && (
          <div className="bg-background/90 border text-[10px] rounded px-2 py-1 shadow-sm text-foreground/80 max-w-[150px] text-center leading-tight">
            Please click <strong>"Stop sharing"</strong> in your browser to finish and upload video.
          </div>
        )}
      </div>
    );
  }

  // Render Analyzer Loader
  if (isAnalyzing) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        {isVoiceRecording ? "Processing Voice..." : "Analyzing Activity..."}
      </Button>
    );
  }

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" data-testid="feedback-button">
            <HeartIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Feedback</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">Send Feedback</h4>
              <p className="text-sm text-muted-foreground">
                Record your screen activity or interactions to better explain your feedback.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {settings.experimentalFeaturesEnabled && (
                <div className="flex items-center space-x-2 pb-2">
                  <Checkbox id="include-video" checked={includeVideo} onCheckedChange={(checked) => setIncludeVideo(checked as boolean)} />
                  <Label htmlFor="include-video" className="cursor-pointer">Include User Actions Recording?</Label>
                </div>
              )}
              <Button onClick={startRecordingFlow} className="w-full">
                <Circle className="h-3 w-3 mr-2 fill-red-500 text-error-text" />
                Add User Actions Recording
              </Button>
              <Button variant="secondary" onClick={() => { setIsPopoverOpen(false); setIsSheetOpen(true); }} className="w-full">
                Submit Manually
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="sm:max-w-md w-full overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Submit Feedback</SheetTitle>
            <SheetDescription>
              Edit the AI-generated report before submitting.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-6 py-4">
            {/* Voice Input Trigger */}
            {settings.experimentalFeaturesEnabled && (
              <div className="flex justify-center">
                <Button
                  variant={isVoiceRecording ? "destructive" : "outline"}
                  onClick={() => {
                    if (isVoiceRecording) {
                      handleVoiceStop(); // UI update
                      toggleVoiceRecording(); // Functional stop
                    } else {
                      toggleVoiceRecording();
                    }
                  }}
                  className={cn("w-full gap-2")}
                >
                  {isVoiceRecording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  {isVoiceRecording ? "Stop Voice Recording" : "Fill with Voice"}
                </Button>
              </div>
            )}

            <RadioGroup
              value={feedbackType}
              onValueChange={(value: FeedbackType) => setFeedbackType(value)}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <RadioGroupItem value="bug" id="bug" className="peer sr-only" />
                <Label
                  htmlFor="bug"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  Report a Bug
                </Label>
              </div>
              <div>
                <RadioGroupItem value="feature" id="feature" className="peer sr-only" />
                <Label
                  htmlFor="feature"
                  className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  Request a Feature
                </Label>
              </div>
            </RadioGroup>

            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder={feedbackType === "bug" ? "Application crashes..." : "Add dark mode..."}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {feedbackType === 'bug' ? (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="steps">Steps to reproduce</Label>
                  <Textarea id="steps" className="h-24" value={steps} onChange={e => setSteps(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expected">Expected behavior</Label>
                  <Textarea id="expected" value={expected} onChange={e => setExpected(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="actual">Actual behavior</Label>
                  <Textarea id="actual" value={actual} onChange={e => setActual(e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description of your idea</Label>
                  <Textarea id="description" className="h-24" value={description} onChange={e => setDescription(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="howItHelps">How would the idea help?</Label>
                  <Textarea id="howItHelps" value={howItHelps} onChange={e => setHowItHelps(e.target.value)} />
                </div>
              </>
            )}

            {includeVideo && (
              <div
                className={cn(
                  "flex items-center gap-2 rounded-md border p-3 text-sm",
                  videoUploadStatus === 'idle' && "border-muted bg-muted/30 text-muted-foreground",
                  videoUploadStatus === 'uploading' && "border-muted bg-muted/50",
                  videoUploadStatus === 'done' && "border-success/50 bg-success text-success-foreground/10 text-success-text",
                  videoUploadStatus === 'failed' && "border-destructive/50 bg-destructive/10 text-destructive",
                  videoUploadStatus === 'skipped' && "border-muted bg-muted/30 text-muted-foreground"
                )}
              >
                {videoUploadStatus === 'idle' && (
                  <span>User actions recording: start a recording to attach</span>
                )}
                {videoUploadStatus === 'uploading' && (
                  <>
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    <span>Uploading user actions recording...</span>
                  </>
                )}
                {videoUploadStatus === 'done' && (
                  <>
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>Video uploaded to Slack</span>
                  </>
                )}
                {videoUploadStatus === 'failed' && (
                  <>
                    <XCircle className="h-4 w-4 shrink-0" />
                    <span>Video upload failed</span>
                  </>
                )}
                {videoUploadStatus === 'skipped' && (
                  <span>No user actions recording (recording was not captured or not started)</span>
                )}
              </div>
            )}

            {includeVideo && videoUploadStatus === 'uploading' && (
              <div className="flex items-center space-x-2 rounded-md border p-3">
                <Checkbox
                  id="submit-without-video"
                  checked={submitWithoutWaitingForVideo}
                  onCheckedChange={(checked) => setSubmitWithoutWaitingForVideo(checked as boolean)}
                />
                <Label htmlFor="submit-without-video" className="cursor-pointer text-sm">
                  Submit without waiting for video upload
                </Label>
              </div>
            )}

            <div className="space-y-4 rounded-md border p-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="allow-contact" checked={allowContact} onCheckedChange={handleAllowContactChange} />
                <Label htmlFor="allow-contact">Can we contact you?</Label>
              </div>
              {allowContact && (
                <div className="grid gap-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    disabled={!!user}
                  />
                </div>
              )}
            </div>
          </div>

          <SheetFooter>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                isAnalyzing ||
                (includeVideo && videoUploadStatus === 'uploading' && !submitWithoutWaitingForVideo)
              }
            >
              {isSubmitting
                ? "Submitting..."
                : includeVideo && videoUploadStatus === 'uploading' && !submitWithoutWaitingForVideo
                  ? "Waiting for video..."
                  : "Submit Feedback"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
